import React, { useState, useRef, useEffect, useCallback } from 'react';
import './CommentBox.css';

export default function CommentBox({ 
  comment, 
  onSave, 
  onDelete, 
  onCancel,
  position,
  containerRef
}) {
  const [text, setText] = useState(comment?.text || '');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [boxPosition, setBoxPosition] = useState({ 
    left: position?.left || 100, 
    top: position?.top || 100 
  });

  const textareaRef = useRef(null);
  const boxRef = useRef(null);
  
  // Box dimensions (matching CSS)
  const boxWidth = 280;
  const boxHeight = 180;

  // Focus textarea on mount
  useEffect(() => {
    console.log('CommentBox mounted:', { comment, position, boxPosition });
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Get container bounds to constrain comment box
  const getContainerBounds = useCallback(() => {
    if (!containerRef?.current) {
      return {
        left: 0,
        top: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
        width: window.innerWidth,
        height: window.innerHeight
      };
    }
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    return {
      left: rect.left,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height
    };
  }, [containerRef]);

  // Adjust initial position to ensure it's within container
  useEffect(() => {
    const containerBounds = getContainerBounds();
    
    const minLeft = containerBounds.left + 10;
    const maxLeft = containerBounds.right - boxWidth - 10;
    const minTop = containerBounds.top + 10;
    const maxTop = containerBounds.bottom - boxHeight - 10;
    
    const constrainedLeft = Math.max(minLeft, Math.min(boxPosition.left, maxLeft));
    const constrainedTop = Math.max(minTop, Math.min(boxPosition.top, maxTop));
    
    setBoxPosition({
      left: constrainedLeft,
      top: constrainedTop
    });
  }, [position, getContainerBounds]);

  // Drag handlers
  const handleMouseDown = useCallback((e) => {
    if (e.target.classList.contains('comment-header') || 
        e.target.closest('.comment-header')) {
      // Don't start drag if clicking the close button
      if (e.target.classList.contains('comment-close-btn') || 
          e.target.closest('.comment-close-btn')) {
        return;
      }
      
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - boxPosition.left,
        y: e.clientY - boxPosition.top
      });
      e.preventDefault();
      e.stopPropagation();
    }
  }, [boxPosition]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const containerBounds = getContainerBounds();
      
      const newLeft = e.clientX - dragOffset.x;
      const newTop = e.clientY - dragOffset.y;
      
      const minLeft = containerBounds.left + 10;
      const maxLeft = containerBounds.right - boxWidth - 10;
      const minTop = containerBounds.top + 10;
      const maxTop = containerBounds.bottom - boxHeight - 10;

      const constrainedLeft = Math.max(minLeft, Math.min(newLeft, maxLeft));
      const constrainedTop = Math.max(minTop, Math.min(newTop, maxTop));
      
      setBoxPosition({
        left: constrainedLeft,
        top: constrainedTop
      });
    }
  }, [isDragging, dragOffset, getContainerBounds]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
    }
  }, [isDragging]);

  // Global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleSave = () => {
    console.log('Saving comment:', comment?.id, text.trim());
    if (text.trim()) {
      onSave(comment.id, text.trim(), boxPosition);
    } else {
      console.log('Empty text, deleting comment:', comment?.id);
      onDelete(comment.id);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const handleCloseClick = () => {
    onCancel();
  };

  const handleDeleteClick = () => {
    onDelete(comment.id);
  };

  return (
    <div
      ref={boxRef}
      className={`comment-box ${isDragging ? 'dragging' : ''}`}
      style={{
        left: `${boxPosition.left}px`,
        top: `${boxPosition.top}px`,
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header with Close Button */}
      <div className="comment-header">
        <div className="comment-header-left">
          <div className="comment-header-title">Comment</div>
          
        </div>
        <button
          className="comment-close-btn"
          onClick={handleCloseClick}
          title="Close (Esc)"
        >
          ×
        </button>
      </div>

      {/* Content Area */}
      <div 
        className="comment-content"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter your comment..."
          className="comment-textarea"
        />
        
        {/* Buttons (Save & Delete only) */}
        <div className="comment-buttons">
          <button
            onClick={handleSave}
            className="comment-button save-button"
          >
            Save
          </button>
          
          <button
            onClick={handleDeleteClick}
            className="comment-button delete-button"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}